import type { UserResponse } from "libs/apiClient";
import { MenuLink } from "types";

type ProductManagementMenuOptions = UserResponse["productManagementOptions"];

function getDefaultProductManagementMenuId(
	productManagementOptions: ProductManagementMenuOptions,
	isSuperAdmin: boolean,
	canShowProviderOperations: boolean,
): string | null {
	if (productManagementOptions != null && Object.values(productManagementOptions).length > 0) {
		if (isSuperAdmin) {
			return MenuLink.NON_PARTNER_CLUB;
		}

		const menu = Object.values(productManagementOptions)[0];
		const subMenuItems = menu?.subMenuItems;
		if (subMenuItems != null) {
			const firstSubMenu = Object.values(subMenuItems)[0];
			if (firstSubMenu?.id != null && firstSubMenu.id !== "") {
				return firstSubMenu.id;
			}
		}

		if (menu?.id != null && menu.id !== "") {
			return menu.id;
		}
	}

	if (canShowProviderOperations) {
		return MenuLink.PROVIDER_OPERATIONS;
	}

	return null;
}

function collectAllowedProductManagementMenuIds(
	productManagementOptions: ProductManagementMenuOptions,
	canShowProviderOperations: boolean,
): Set<string> {
	const allowedMenuIds = new Set<string>();

	for (const menu of Object.values(productManagementOptions ?? {})) {
		if (menu == null) {
			continue;
		}

		if (menu.id != null && menu.id !== "") {
			allowedMenuIds.add(menu.id);
		}

		const subMenuItems = menu.subMenuItems;
		if (subMenuItems == null) {
			continue;
		}

		for (const subMenu of Object.values(subMenuItems)) {
			if (subMenu?.id != null && subMenu.id !== "") {
				allowedMenuIds.add(subMenu.id);
			}
		}
	}

	if (canShowProviderOperations) {
		allowedMenuIds.add(MenuLink.PROVIDER_OPERATIONS);
	}

	return allowedMenuIds;
}

export function resolveProductManagementActiveMenu({
	requestedMenu,
	canShowProviderOperations,
	productManagementOptions,
	isSuperAdmin,
}: {
	requestedMenu: string | null;
	canShowProviderOperations: boolean;
	productManagementOptions: ProductManagementMenuOptions;
	isSuperAdmin: boolean;
}): string | null {
	const defaultMenuId = getDefaultProductManagementMenuId(
		productManagementOptions,
		isSuperAdmin,
		canShowProviderOperations,
	);
	const allowedMenuIds = collectAllowedProductManagementMenuIds(
		productManagementOptions,
		canShowProviderOperations,
	);

	if (requestedMenu != null && requestedMenu !== "" && allowedMenuIds.has(requestedMenu)) {
		return requestedMenu;
	}

	return defaultMenuId;
}
